import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Show, UserButton } from "@clerk/react";
import { Search, Plus, MessageCircle, User, Home, LayoutGrid, Truck, Shield, Briefcase, Wrench, Wallet } from "lucide-react";
import { useMe } from "@/context/MeContext";

const navCls = ({ isActive }) =>
  `text-sm font-medium transition-colors duration-200 ${isActive ? "text-forest" : "text-stone-600 hover:text-forest"}`;

function SearchBox() {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); navigate(`/catalogue?q=${encodeURIComponent(q)}`); }}
      className="hidden md:flex items-center gap-2 bg-white border border-stone-200 rounded-full px-4 py-2 w-72 focus-within:ring-2 focus-within:ring-forest/30"
    >
      <Search className="w-4 h-4 text-stone-400" aria-hidden="true" />
      <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chaise, lampe, vélo…" className="bg-transparent outline-none text-sm w-full" />
    </form>
  );
}

function Header() {
  const { me } = useMe();
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-sand/80 border-b border-stone-200/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-6">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 font-sans text-2xl font-extrabold tracking-tight shrink-0">
          <img src="/img/logo-emblem.png" alt="" className="h-11 w-auto" />
          <span className="text-[#5E8C61]">labradedie<span className="text-[#F28C6B]">.fr</span></span>
        </Link>
        <nav className="hidden lg:flex items-center gap-6">
          <NavLink to="/catalogue" className={navCls} data-testid="nav-catalogue">Catalogue</NavLink>
          <NavLink to="/debarras" className={navCls} data-testid="nav-debarras">Débarras</NavLink>
          <NavLink to="/livraison" className={navCls} data-testid="nav-livraison">Livraison</NavLink>
          {me?.role === "pro" && <NavLink to="/pro" className={navCls} data-testid="nav-pro">Espace pro</NavLink>}
          {me?.role === "provider" && <NavLink to="/prestataire" className={navCls} data-testid="nav-provider">Espace prestataire</NavLink>}
          {me?.role === "admin" && <NavLink to="/admin" className={navCls} data-testid="nav-admin">Administration</NavLink>}
        </nav>
        <div className="flex-1" />
        <SearchBox />
        <Link to="/publier" data-testid="header-publish-btn" className="hidden sm:inline-flex items-center gap-2 rounded-full bg-terra text-white px-4 py-2 text-sm font-semibold hover:-translate-y-0.5 hover:shadow-md transition-transform duration-200">
          <Plus className="w-4 h-4" aria-hidden="true" /> Publier
        </Link>
        <Show when="signed-in">
          <Link to="/compte?tab=wallet" data-testid="header-wallet-link" className="text-stone-600 hover:text-forest" aria-label="Porte-monnaie"><Wallet className="w-5 h-5" /></Link>
          <Link to="/messages" data-testid="header-messages-link" className="text-stone-600 hover:text-forest" aria-label="Messages"><MessageCircle className="w-5 h-5" /></Link>
          <Link to="/compte" data-testid="header-account-link" className="text-stone-600 hover:text-forest" aria-label="Mon compte"><User className="w-5 h-5" /></Link>
          <UserButton />
        </Show>
        <Show when="signed-out">
          <Link to="/sign-in" data-testid="header-signin-btn" className="rounded-full border border-forest text-forest px-4 py-2 text-sm font-semibold hover:bg-forest hover:text-white transition-colors duration-200">
            Connexion
          </Link>
        </Show>
      </div>
      <div className="awning" aria-hidden="true" />
    </header>
  );
}

function MobileNav() {
  const { me } = useMe();
  const item = "flex flex-col items-center gap-0.5 text-[11px] font-medium";
  const cls = ({ isActive }) => `${item} ${isActive ? "text-forest" : "text-stone-500"}`;
  const extra = me?.role === "admin" ? ["/admin", Shield, "Admin"] : me?.role === "pro" ? ["/pro", Briefcase, "Pro"] : me?.role === "provider" ? ["/prestataire", Wrench, "Missions"] : ["/livraison", Truck, "Livraison"];
  const [to, Icon, label] = extra;
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/90 backdrop-blur-xl border-t border-stone-200 h-16 grid grid-cols-5 items-center" data-testid="mobile-nav">
      <NavLink to="/" end className={cls}><Home className="w-5 h-5" />Accueil</NavLink>
      <NavLink to="/catalogue" className={cls}><LayoutGrid className="w-5 h-5" />Catalogue</NavLink>
      <NavLink to="/publier" className={cls}><Plus className="w-5 h-5" />Publier</NavLink>
      <NavLink to="/messages" className={cls}><MessageCircle className="w-5 h-5" />Messages</NavLink>
      <NavLink to={to} className={cls}><Icon className="w-5 h-5" />{label}</NavLink>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="mt-24 pb-24 lg:pb-0 bg-navy text-white/80">
      <div className="bunting" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid gap-8 md:grid-cols-3 text-sm">
        <div>
          <img src="/img/logo-full.png" alt="labradedie.fr" data-testid="footer-logo" className="w-56 rounded-2xl bg-white p-3" />
          <p className="mt-2 max-w-xs">Vendre, donner, débarrasser et faire livrer entre voisins. La braderie de toute la France.</p>
        </div>
        <div className="flex flex-col gap-2">
          <Link to="/legal/cgv" data-testid="footer-cgv">Conditions générales (CGU/CGV)</Link>
          <Link to="/legal/confidentialite" data-testid="footer-privacy">Politique de confidentialité</Link>
          <Link to="/legal/interdits" data-testid="footer-banned">Objets interdits</Link>
        </div>
        <p className="text-xs text-white/60">Version pilote. Livraison et débarras en mode simulation : aucune mission réelle n'est créée auprès d'un transporteur.</p>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-sand flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <MobileNav />
    </div>
  );
}
